import * as React from 'react';
import { type Aggregate, aggregate } from '../../../../shared/riskregister/aggregate';
import { filteredRows } from '../../../../shared/riskregister/filters';
import { groupKey, groupLabel } from '../../../../shared/riskregister/grouping';
import { sortRows } from '../../../../shared/riskregister/sorting';
import type { Payload, Row, ViewState } from '../../../../shared/riskregister/types';

export interface GroupedRows {
  key: string;
  label: string;
  rows: Row[];
  aggregate: Aggregate;
  confidence: string | null;
}

export interface FilteredRiskRegisterRows {
  rows: Row[];
  groups: GroupedRows[];
}

export function buildGroupedRows(rows: Row[], state: ViewState, payload: Payload): GroupedRows[] {
  const orderedRows = sortRows(rows, state.sort);
  const groups = new Map<string, Row[]>();

  orderedRows.forEach((row) => {
    const key = groupKey(row, state.group);
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  });

  return Array.from(groups, ([key, groupRows]) => {
    const confidence = groupRows.every((row) => row.conf === groupRows[0].conf) ? groupRows[0].conf : null;
    return {
      key,
      label: groupLabel(key, state.group, payload),
      rows: groupRows,
      aggregate: aggregate(groupRows, payload),
      confidence,
    };
  });
}

export function useFilteredRows(payload: Payload, state: ViewState): FilteredRiskRegisterRows {
  return React.useMemo(() => {
    const rows = filteredRows(payload.rows, state, payload);
    return { rows, groups: buildGroupedRows(rows, state, payload) };
  }, [payload, state]);
}
